/**
 * Transforms webhook response data to chart-compatible format
 * Handles conversion from Make.com webhook format to internal chart data structure
 */

import { ChartCacheData, MetalChartData, ChartDataPoint, PredictionScenarios } from './chartCache';

/**
 * Expected webhook response structure from Make.com
 */
interface WebhookResponse {
  previsione_oro?: {
    markdown: string;
    dati_grafici?: {
      prezzo_attuale?: number;
      prezzo_target?: number;
      percentuale_target?: number;
      timeframe?: string;
      confidence?: number;
      serie_temporale?: Array<{
        data: string;
        timestamp: number;
        storico?: number;
        previsto_alto?: number;
        previsto_medio?: number;
        previsto_basso?: number;
        alto?: number;
        basso?: number;
      }>;
      scenari?: {
        alto: { prezzo: number; percentuale: number; probabilita: number };
        medio: { prezzo: number; percentuale: number; probabilita: number };
        basso: { prezzo: number; percentuale: number; probabilita: number };
      };
    };
  };
  previsione_argento?: {
    markdown: string;
    dati_grafici?: {
      prezzo_attuale?: number;
      prezzo_target?: number;
      percentuale_target?: number;
      timeframe?: string;
      confidence?: number;
      serie_temporale?: Array<{
        data: string;
        timestamp: number;
        storico?: number;
        previsto_alto?: number;
        previsto_medio?: number;
        previsto_basso?: number;
        alto?: number;
        basso?: number;
      }>;
      scenari?: {
        alto: { prezzo: number; percentuale: number; probabilita: number };
        medio: { prezzo: number; percentuale: number; probabilita: number };
        basso: { prezzo: number; percentuale: number; probabilita: number };
      };
    };
  };
}

/**
 * Transforms webhook time series data to chart format
 */
const transformTimeSeries = (serieTemporale: any[]): ChartDataPoint[] => {
  if (!Array.isArray(serieTemporale)) {
    console.warn('[WebhookTransformer] ⚠️ Invalid time series data, not an array');
    return [];
  }

  const transformedData = serieTemporale.map(point => ({
    date: point.data || new Date().toISOString(),
    timestamp: point.timestamp || Date.now(),
    historical: point.storico,
    predictedHigh: point.previsto_alto || point.alto,
    predictedAverage: point.previsto_medio,
    predictedLow: point.previsto_basso || point.basso,
    high: point.alto,
    low: point.basso
  }));

  // Ordina i dati per data crescente per garantire che l'asse X sia ordinato correttamente
  return transformedData.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateA - dateB;
  });
};

/**
 * Transforms webhook scenarios data to chart format
 */
const transformScenarios = (scenari: any): PredictionScenarios => {
  const defaultScenario = { price: 0, percentage: 0, probability: 0 };
  
  if (!scenari || typeof scenari !== 'object') {
    console.warn('[WebhookTransformer] ⚠️ Invalid scenarios data');
    return {
      high: defaultScenario,
      average: defaultScenario,
      low: defaultScenario
    };
  }

  return {
    high: {
      price: scenari.alto?.prezzo || 0,
      percentage: scenari.alto?.percentuale || 0,
      probability: scenari.alto?.probabilita || 0
    },
    average: {
      price: scenari.medio?.prezzo || 0,
      percentage: scenari.medio?.percentuale || 0,
      probability: scenari.medio?.probabilita || 0
    },
    low: {
      price: scenari.basso?.prezzo || 0,
      percentage: scenari.basso?.percentuale || 0,
      probability: scenari.basso?.probabilita || 0
    }
  };
};

/**
 * Transforms single metal prediction data
 */
const transformMetalData = (
  metal: 'ORO' | 'ARGENTO',
  datiGrafici: any
): MetalChartData | null => {
  if (!datiGrafici || typeof datiGrafici !== 'object') {
    console.warn(`[WebhookTransformer] ⚠️ No chart data for ${metal}`);
    return null;
  }

  const timeSeries = datiGrafici.serie_temporale 
    ? transformTimeSeries(datiGrafici.serie_temporale)
    : [];

  const scenarios = transformScenarios(datiGrafici.scenari);

  return {
    metal,
    currentPrice: datiGrafici.prezzo_attuale || 0,
    targetPrice: datiGrafici.prezzo_target || 0,
    targetPercentage: datiGrafici.percentuale_target || 0,
    timeframe: datiGrafici.timeframe || '7 giorni',
    confidence: datiGrafici.confidence || 0,
    timeSeries,
    scenarios
  };
};

/**
 * Main transformation function
 * Converts webhook response to chart cache format
 */
export const transformWebhookToPredictionCharts = (webhookData: any): ChartCacheData => {
  console.log('[WebhookTransformer] 🔄 Transforming webhook data to chart format');
  
  if (!webhookData || typeof webhookData !== 'object') {
    console.error('[WebhookTransformer] ❌ Invalid webhook data');
    return { gold: null, silver: null };
  }

  // Transform gold data
  let goldData: MetalChartData | null = null;
  if (webhookData.previsione_oro?.dati_grafici) {
    goldData = transformMetalData('ORO', webhookData.previsione_oro.dati_grafici);
    console.log('[WebhookTransformer] 🥇 Gold chart data transformed:', !!goldData);
  }

  // Transform silver data
  let silverData: MetalChartData | null = null;
  if (webhookData.previsione_argento?.dati_grafici) {
    silverData = transformMetalData('ARGENTO', webhookData.previsione_argento.dati_grafici);
    console.log('[WebhookTransformer] 🥈 Silver chart data transformed:', !!silverData);
  }

  const result: ChartCacheData = {
    gold: goldData,
    silver: silverData
  };

  console.log('[WebhookTransformer] ✅ Transformation completed', {
    hasGold: !!result.gold,
    hasSilver: !!result.silver,
    goldTimeSeries: result.gold?.timeSeries?.length || 0,
    silverTimeSeries: result.silver?.timeSeries?.length || 0
  });

  return result;
};

/**
 * Validates webhook data structure
 */
export const validateWebhookData = (webhookData: any): boolean => {
  if (!webhookData || typeof webhookData !== 'object') {
    return false;
  }

  const hasGoldData = webhookData.previsione_oro?.dati_grafici;
  const hasSilverData = webhookData.previsione_argento?.dati_grafici;

  return !!(hasGoldData || hasSilverData);
};

/**
 * Gets transformation summary for debugging
 */
export const getTransformationSummary = (webhookData: any) => {
  const hasGold = !!(webhookData?.previsione_oro?.dati_grafici);
  const hasSilver = !!(webhookData?.previsione_argento?.dati_grafici);
  
  const goldTimeSeries = webhookData?.previsione_oro?.dati_grafici?.serie_temporale?.length || 0;
  const silverTimeSeries = webhookData?.previsione_argento?.dati_grafici?.serie_temporale?.length || 0;

  return {
    isValid: hasGold || hasSilver,
    hasGold,
    hasSilver,
    goldTimeSeries,
    silverTimeSeries,
    goldScenarios: !!(webhookData?.previsione_oro?.dati_grafici?.scenari),
    silverScenarios: !!(webhookData?.previsione_argento?.dati_grafici?.scenari)
  };
};
